import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MonteurFields from "@/components/MonteurFields";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import JobAttachments from "@/components/JobAttachments";
import ChatDialog from "@/components/ChatDialog";
import { PortfolioManager } from "@/components/PortfolioManager";
import { typesLabel, pricingLabel, WERK_TYPES, PROVINCIES, OP_ZOEK_NAAR } from "@/lib/jobOptions";
import { ClipboardList, Inbox, CheckSquare, User, MapPin, Euro, Calendar, Clock, CalendarDays, Filter, MessageCircle, BadgeCheck, Crown } from "lucide-react";
import { startCheckout } from "@/lib/payments";
import { PromoBar } from "@/components/PromoBar";
import { FreeBanner } from "@/components/FreeBanner";
import { AdSlot } from "@/components/AdSlot";
import { isFreePeriod } from "@/lib/pricing";
import { toast } from "sonner";

const TABS = [
  { key: "bord", label: "Opdrachtenbord", icon: ClipboardList },
  { key: "aanvragen", label: "Aanvragen", icon: Inbox },
  { key: "mijn", label: "Mijn opdrachten", icon: CheckSquare },
  { key: "agenda", label: "Mijn agenda", icon: CalendarDays },
  { key: "profiel", label: "Mijn profiel", icon: User },
];

function JobCard({ job, children }) {
  return (
    <div data-testid="job-card" className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-display text-lg font-bold text-slate-950">{job.titel}</h3>
        <StatusBadge status={job.mijn_status || job.status} />
      </div>
      <p className="text-sm text-slate-500 mb-3 flex items-center gap-1.5">
        {job.company_name}
        {job.company_geverifieerd && (
          <span data-testid="verified-badge" title="BTW-nummer geverifieerd via VIES"
            className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-sm">
            <BadgeCheck className="w-3.5 h-3.5" /> Geverifieerd
          </span>
        )}
      </p>
      <p className="text-sm text-slate-600 mb-4 line-clamp-2">{job.beschrijving}</p>
      <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.locatie}</span>
        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {job.start_datum}</span>
        {job.provincie && (
          <span data-testid="job-provincie-label" className="flex items-center gap-1 col-span-2 font-semibold text-slate-600">
            <MapPin className="w-3.5 h-3.5 text-orange-500" /> {job.provincie}
          </span>
        )}
        <span className="flex items-center gap-1 col-span-2"><Euro className="w-3.5 h-3.5" /> {pricingLabel(job)}</span>
      </div>
      <span className="inline-block mb-3 text-xs font-bold uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm">
        {typesLabel(job)}
      </span>
      {(job.op_zoek_naar || []).length > 0 && (
        <div data-testid="job-op-zoek-labels" className="flex flex-wrap gap-1.5 mb-3">
          {job.op_zoek_naar.map((o) => (
            <span key={o} className="text-xs font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-sm">
              {o}
            </span>
          ))}
        </div>
      )}
      {(job.bijlagen || []).length > 0 && (
        <JobAttachments bijlagen={job.bijlagen} />
      )}
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="col-span-full text-center py-16 text-slate-400 border border-dashed border-slate-300 rounded-md">{text}</div>;
}

export default function MonteurDashboard() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("bord");
  const [jobs, setJobs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [mine, setMine] = useState([]);
  const [profile, setProfile] = useState(user);
  const [agenda, setAgenda] = useState({});
  const [jobTypeFilter, setJobTypeFilter] = useState("alle");
  const [provincieFilter, setProvincieFilter] = useState("alle");
  const [ozFilter, setOzFilter] = useState("alle");
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [chatJob, setChatJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, m, a] = await Promise.all([
        api.get("/hire-requests"),
        api.get("/jobs/mijn-opdrachten"),
        api.get("/agenda/me"),
      ]);
      setRequests(r.data.requests);
      setMine(m.data.jobs);
      setAgenda(a.data.agenda);
    } catch (e) {
      toast.error("Laden mislukt");
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const params = jobTypeFilter !== "alle" ? { type_werk: jobTypeFilter } : {};
      const { data } = await api.get("/jobs", { params });
      setJobs(data.jobs);
    } catch (e) {
      toast.error("Opdrachten laden mislukt");
    }
  }, [jobTypeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const setAgendaDay = async (datum, status) => {
    try {
      const { data } = await api.put("/agenda/me", { datum, status });
      setAgenda(data.agenda);
    } catch (e) {
      toast.error("Agenda bijwerken mislukt");
    }
  };

  const showInterest = async (id) => {
    try {
      await api.post(`/jobs/${id}/interesse`);
      toast.success("Je interesse is doorgegeven aan de opdrachtgever!");
      load();
      loadJobs();
      setTab("mijn");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const respond = async (id, actie) => {
    try {
      await api.post(`/hire-requests/${id}/respond`, { actie });
      toast.success(actie === "accepteren" ? "Aanvraag geaccepteerd" : "Aanvraag geweigerd");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const finishJob = async (id) => {
    try {
      await api.put(`/jobs/${id}/afronden`);
      toast.success("Opdracht afgerond");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/monteurs/me", {
        name: profile.name, telefoon: profile.telefoon, regio: profile.regio,
        specialisatie: profile.specialisatie, bio: profile.bio,
        ervaring_jaren: parseInt(profile.ervaring_jaren || 0, 10),
        uurtarief: parseFloat(profile.uurtarief || 0),
        beschikbaar: profile.beschikbaar,
        werkstatuut: profile.werkstatuut || "",
        vervoer: profile.vervoer || "",
        rijbewijs_europa: !!profile.rijbewijs_europa,
        rijbewijs_categorieen: profile.rijbewijs_categorieen || [],
        werkmateriaal: profile.werkmateriaal || "",
        spec_montage: profile.spec_montage || [],
        spec_montage_andere: profile.spec_montage_andere || "",
        spec_afwerking: profile.spec_afwerking || [],
        spec_afwerking_andere: profile.spec_afwerking_andere || "",
        leiding_team: !!profile.leiding_team,
        talen: profile.talen || [],
        moedertaal: profile.moedertaal || "",
        andere_talen: profile.andere_talen || "",
      });
      refreshUser(data.user);
      toast.success("Profiel opgeslagen");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleBeschikbaar = async (val) => {
    setProfile({ ...profile, beschikbaar: val });
    try {
      const { data } = await api.put("/monteurs/me", { beschikbaar: val });
      refreshUser(data.user);
      toast.success(val ? "Je bent nu beschikbaar" : "Je bent op niet-beschikbaar gezet");
    } catch (e) {
      toast.error("Bijwerken mislukt");
    }
  };

  const sub = { bord: "Beschikbare opdrachten", aanvragen: "Directe aanvragen", mijn: "Toegewezen werk", agenda: "Beschikbaarheidsrooster", profiel: "Account & beschikbaarheid" }[tab];
  const title = { bord: "Opdrachtenbord", aanvragen: "Mijn aanvragen", mijn: "Mijn opdrachten", agenda: "Mijn agenda", profiel: "Mijn profiel" }[tab];
  const visibleJobs = (onlyVerified ? jobs.filter((j) => j.company_geverifieerd) : jobs)
    .filter((j) => provincieFilter === "alle" || j.provincie === provincieFilter)
    .filter((j) => ozFilter === "alle" || (j.op_zoek_naar || []).includes(ozFilter))
    .slice()
    .sort((a, b) => (b.company_geverifieerd ? 1 : 0) - (a.company_geverifieerd ? 1 : 0));
  const proPrice = user?.pro_intro_eligible ? "1e kwartaal €1" : "€19/kwartaal";

  return (
    <DashboardLayout title={title} subtitle={`Backoffice · ${sub}`} tabs={TABS} active={tab} onTab={setTab}>
      <AdSlot slot="dashboard_top" rol="monteur" provincie={user?.provincie} className="mb-5 max-w-3xl" />
      {!isFreePeriod() && user?.pro_intro_eligible && (
        <div className="mb-5 rounded-md overflow-hidden">
          <PromoBar onClick={() => startCheckout({ kind: "pro" })} />
        </div>
      )}
      {tab === "bord" && (
        <div className="space-y-5">
          {isFreePeriod() && <FreeBanner />}
          {!isFreePeriod() && !user?.pro && (
            <div data-testid="monteur-upgrade-banner" className="flex flex-wrap items-center gap-3 bg-slate-900 text-white rounded-md px-4 py-3 text-sm">
              <Crown className="w-5 h-5 shrink-0 text-orange-400" />
              <span className="font-semibold flex-1 min-w-[200px]">Je Pro-toegang is verlopen. Word Pro om op opdrachten te reageren en bovenaan in de gids te staan. {user?.pro_intro_eligible && "Nu je eerste kwartaal voor maar €1!"}</span>
              <Button data-testid="monteur-upgrade-btn" onClick={() => startCheckout({ kind: "pro" })}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold shrink-0">
                Word Pro · {proPrice}
              </Button>
            </div>
          )}
          {!isFreePeriod() && user?.pro && user?.pro_until && (() => {
            const daysLeft = Math.ceil((new Date(user.pro_until) - new Date()) / 86400000);
            if (daysLeft <= 7) {
              return (
                <div data-testid="monteur-pro-expiring-banner" className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-4 py-3 text-sm">
                  <Crown className="w-5 h-5 shrink-0 text-amber-600" />
                  <span className="font-semibold flex-1 min-w-[200px]">
                    Je Pro-toegang verloopt {daysLeft <= 0 ? "vandaag" : daysLeft === 1 ? "morgen" : `over ${daysLeft} dagen`} ({new Date(user.pro_until).toLocaleDateString("nl-NL")}). Verleng nu om actief te blijven.
                  </span>
                  <Button data-testid="monteur-renew-btn" onClick={() => startCheckout({ kind: "pro" })}
                    className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm font-bold shrink-0">
                    Verleng · {proPrice}
                  </Button>
                </div>
              );
            }
            return (
              <div data-testid="monteur-pro-banner" className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 rounded-md px-4 py-2.5 text-sm font-semibold">
                <Crown className="w-4 h-4 shrink-0" />
                Pro actief tot {new Date(user.pro_until).toLocaleDateString("nl-NL")}.
              </div>
            );
          })()}
          <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Filter op type werk</Label>
              <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                <SelectTrigger data-testid="filter-type-werk" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle types</SelectItem>
                  {WERK_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {jobTypeFilter !== "alle" && (
              <Button data-testid="clear-type-filter-btn" variant="outline" onClick={() => setJobTypeFilter("alle")}
                className="rounded-sm border-slate-300 font-semibold">Wissen</Button>
            )}
            <div className="w-52">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Provincie</Label>
              <Select value={provincieFilter} onValueChange={setProvincieFilter}>
                <SelectTrigger data-testid="filter-provincie-bord" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle provincies</SelectItem>
                  {PROVINCIES.map((g) => (
                    <div key={g.groep}>
                      <div className="px-2 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">{g.groep}</div>
                      {g.opties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-60">
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Op zoek naar</Label>
              <Select value={ozFilter} onValueChange={setOzFilter}>
                <SelectTrigger data-testid="filter-opzoek-bord" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle types</SelectItem>
                  {OP_ZOEK_NAAR.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <span data-testid="jobs-count" className="text-xs font-semibold text-slate-500 mr-1">
                {visibleJobs.length} van {jobs.length} opdrachten
              </span>
              {(jobTypeFilter !== "alle" || provincieFilter !== "alle" || ozFilter !== "alle" || onlyVerified) && (
                <Button data-testid="clear-all-filters-btn" variant="outline" size="sm"
                  onClick={() => { setJobTypeFilter("alle"); setProvincieFilter("alle"); setOzFilter("alle"); setOnlyVerified(false); }}
                  className="rounded-sm border-slate-300 font-semibold text-xs h-8">
                  Wis filters
                </Button>
              )}
              <Switch data-testid="filter-verified-toggle" checked={onlyVerified} onCheckedChange={setOnlyVerified} />
              <Label className="text-xs font-bold text-slate-700 flex items-center gap-1 cursor-pointer" onClick={() => setOnlyVerified(!onlyVerified)}>
                <BadgeCheck className="w-3.5 h-3.5 text-green-600" /> Alleen geverifieerde bedrijven
              </Label>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleJobs.length === 0 && <Empty text="Geen open opdrachten met dit filter." />}
            {visibleJobs.map((job) => (
              <JobCard key={job.id} job={job}>
                {job.mijn_interesse ? (
                  <Button data-testid="interesse-getoond-btn" disabled
                    className="w-full bg-green-100 text-green-700 rounded-sm font-bold cursor-default hover:bg-green-100">
                    ✓ Interesse getoond
                  </Button>
                ) : user?.pro ? (
                  <Button data-testid="accept-job-btn" onClick={() => showInterest(job.id)}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold text-sm leading-tight whitespace-normal h-auto py-2.5">
                    Laat weten dat je interesse hebt
                  </Button>
                ) : (
                  <Button data-testid="upgrade-to-react-btn" onClick={() => startCheckout({ kind: "pro" })}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold">
                    <Crown className="w-4 h-4 mr-1 text-orange-400" /> Word Pro om te reageren
                  </Button>
                )}
              </JobCard>
            ))}
          </div>
        </div>
      )}

      {tab === "aanvragen" && (
        <div className="grid md:grid-cols-2 gap-4">
          {requests.length === 0 && <Empty text="Je hebt nog geen aanvragen ontvangen." />}
          {requests.map((r) => (
            <div key={r.id} data-testid="hire-request-card" className="bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display text-lg font-bold text-slate-950">{r.titel}</h3>
                <StatusBadge status={r.status} />
              </div>
              <p className="text-sm font-semibold text-orange-600 mb-2 flex items-center gap-1.5">
                {r.company_name}
                {r.company_geverifieerd && (
                  <span data-testid="verified-badge" title="BTW-nummer geverifieerd via VIES"
                    className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-sm">
                    <BadgeCheck className="w-3.5 h-3.5" /> Geverifieerd
                  </span>
                )}
              </p>
              <p className="text-sm text-slate-600 mb-4">{r.bericht}</p>
              <JobAttachments bijlagen={r.bijlagen} />
              {r.status === "open" && (
                <div className="flex gap-2">
                  <Button data-testid="accept-request-btn" onClick={() => respond(r.id, "accepteren")}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
                    Accepteren
                  </Button>
                  <Button data-testid="decline-request-btn" onClick={() => respond(r.id, "weigeren")}
                    variant="outline" className="flex-1 rounded-sm font-bold border-slate-300">
                    Weigeren
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "mijn" && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {mine.length === 0 && <Empty text="Je hebt nog geen opdrachten waarvoor je interesse toonde." />}
          {mine.map((job) => (
            <JobCard key={job.id} job={job}>
              <div className="space-y-2">
                {job.mijn_status !== "niet_gekozen" && (
                  <Button data-testid="chat-open-btn" onClick={() => setChatJob(job)}
                    className="w-full bg-slate-950 hover:bg-orange-500 text-white rounded-sm font-bold transition-colors">
                    <MessageCircle className="w-4 h-4 mr-2" /> Chat met opdrachtgever
                  </Button>
                )}
                {job.mijn_status === "toegewezen" && (
                  <Button data-testid="finish-job-btn" onClick={() => finishJob(job.id)}
                    variant="outline" className="w-full rounded-sm font-bold border-slate-300">
                    Markeer als afgerond
                  </Button>
                )}
              </div>
            </JobCard>
          ))}
        </div>
      )}

      {tab === "agenda" && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 max-w-md">
            Duid hier aan op welke dagen je beschikbaar bent. Bedrijven zien deze agenda wanneer ze je profiel bekijken.
          </p>
          <AvailabilityCalendar agenda={agenda} editable onSet={setAgendaDay} />
        </div>
      )}

      {tab === "profiel" && (
        <div className="max-w-2xl space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-5 flex items-center justify-between">
            <div>
              <p className="font-display font-bold text-slate-950">Beschikbaarheid</p>
              <p className="text-sm text-slate-500">Toon je in de professionalsgids voor bedrijven</p>
            </div>
            <Switch data-testid="beschikbaar-switch" checked={!!profile.beschikbaar} onCheckedChange={toggleBeschikbaar} />
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold text-slate-700">Naam</Label>
                <Input data-testid="profile-name" value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Telefoon</Label>
                <Input data-testid="profile-telefoon" value={profile.telefoon || ""} onChange={(e) => setProfile({ ...profile, telefoon: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Regio</Label>
                <Input data-testid="profile-regio" value={profile.regio || ""} onChange={(e) => setProfile({ ...profile, regio: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Specialisatie</Label>
                <Select value={profile.specialisatie || "beide"} onValueChange={(v) => setProfile({ ...profile, specialisatie: v })}>
                  <SelectTrigger data-testid="profile-specialisatie" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ramen">Ramen</SelectItem>
                    <SelectItem value="deuren">Deuren</SelectItem>
                    <SelectItem value="beide">Ramen & Deuren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Ervaring (jaren)</Label>
                <Input data-testid="profile-ervaring" type="number" value={profile.ervaring_jaren || ""} onChange={(e) => setProfile({ ...profile, ervaring_jaren: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700">Uurtarief (€)</Label>
                <Input data-testid="profile-uurtarief" type="number" value={profile.uurtarief || ""} onChange={(e) => setProfile({ ...profile, uurtarief: e.target.value })} className="rounded-sm border-slate-300" />
              </div>
            </div>
            <MonteurFields data={profile} onChange={setProfile} />
            <Button data-testid="save-profile-btn" onClick={saveProfile} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold">
              Profiel opslaan
            </Button>
          </div>

          <PortfolioManager />
        </div>
      )}

      <ChatDialog job={chatJob} currentUserId={user?.id} monteurMode open={!!chatJob} onClose={() => setChatJob(null)} />
    </DashboardLayout>
  );
}
